import { ArrowLeft, Heart, Footprints, Activity, Moon, Scale, Brain, BarChart3, MessageSquare, Layers, Shield, Code, Smartphone, Zap, Award } from 'lucide-react'
import { Link } from 'react-router-dom'

const features = [
  {
    icon: <Footprints size={24} />,
    title: 'Steps Tracker',
    description: 'Real-time step counting synchronized with Health Connect. Daily goals, progress rings, and weekly trends with animated visualizations.',
  },
  {
    icon: <Activity size={24} />,
    title: 'Calories & Distance',
    description: 'Automatic calorie burn estimation and distance tracking based on步 activity data, stride length calibration, and intensity zones.',
  },
  {
    icon: <Heart size={24} />,
    title: 'Heart Rate Monitoring',
    description: 'Continuous heart rate data from Health Connect with resting HR trends, zone analysis, and cardiovascular fitness insights.',
  },
  {
    icon: <Moon size={24} />,
    title: 'Sleep Tracking',
    description: 'Sleep stage analysis (light, deep, REM, awake) with duration tracking, consistency scoring, and sleep quality trends.',
  },
  {
    icon: <Scale size={24} />,
    title: 'Weight & BMI',
    description: 'Manual weight logging with BMI calculation, goal tracking, and trend visualization over customizable time ranges.',
  },
  {
    icon: <BarChart3 size={24} />,
    title: 'Weekly Charts',
    description: 'Beautiful animated charts showing weekly trends across all metrics with interactive tooltips and comparative analysis.',
  },
  {
    icon: <MessageSquare size={24} />,
    title: 'AI Fitness Q&A',
    description: 'Natural language fitness coaching powered by a three-tier AI fallback chain. Ask about workouts, nutrition, recovery, and more.',
  },
]

const systemStack = [
  { category: 'Language', tech: 'Kotlin', purpose: 'Primary language with coroutines for async Health Connect operations' },
  { category: 'UI Framework', tech: 'Jetpack Compose', purpose: 'Declarative UI with Material 3 theming, animations, and responsive layouts' },
  { category: 'DI Framework', tech: 'Hilt', purpose: 'Compile-time dependency injection for clean architecture and testability' },
  { category: 'Health Data', tech: 'Health Connect API', purpose: 'Centralized health data aggregation from multiple wearable sources' },
  { category: 'AI Engine', tech: 'Gemini API', purpose: 'Tier 3 fallback for fitness coaching Q&A with domain-specific prompting' },
  { category: 'Storage', tech: 'DataStore (Proto)', purpose: 'Type-safe preference and settings storage with reactive streams' },
  { category: 'Navigation', tech: 'Navigation Compose', purpose: 'Type-safe navigation graph with deep link support and back stack management' },
]

const aiTiers = [
  {
    tier: 'Tier 1',
    name: 'OpenRouter',
    model: 'Multiple providers',
    status: 'Primary',
    description: 'Routes requests through OpenRouter for maximum model flexibility and fallback options across providers.',
  },
  {
    tier: 'Tier 2',
    name: 'NVIDIA NIM',
    model: 'Optimized inference',
    status: 'Secondary',
    description: 'High-performance inference via NVIDIA NIM with GPU-accelerated response generation for low-latency answers.',
  },
  {
    tier: 'Tier 3',
    name: 'Gemini',
    model: 'Google AI',
    status: 'Tertiary',
    description: 'Google Gemini as the final fallback with strong reasoning capabilities and built-in fitness domain knowledge.',
  },
]

export default function FitnessTrainerPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-background/80 backdrop-blur-xl border-b border-white/5">
        <div className="flex justify-between items-center px-8 py-4 max-w-7xl mx-auto">
          <Link to="/" className="text-xl font-bold tracking-tighter text-white font-headline">
            Neural Slate
          </Link>
          <Link
            to="/#projects"
            className="text-on-surface-variant hover:text-primary transition-colors flex items-center gap-2"
          >
            <ArrowLeft size={16} />
            Back to Projects
          </Link>
        </div>
      </header>

      <main className="pt-24">
        {/* Hero Section */}
        <section className="relative min-h-[600px] flex items-center px-8 overflow-hidden">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-tertiary/10 to-transparent blur-3xl pointer-events-none"></div>
          <div className="max-w-7xl mx-auto w-full relative z-10">
            <div className="max-w-4xl">
              <span className="inline-block py-1 px-3 mb-6 bg-surface-container-high border border-outline-variant/20 rounded-full text-primary text-xs font-mono tracking-widest uppercase">
                Android App · Native Kotlin
              </span>
              <h1 className="text-5xl md:text-7xl font-bold tracking-tighter mb-8 leading-tight font-headline text-white">
                AI Fitness Trainer <br />
                <span className="text-primary">Health Connect Companion</span>
              </h1>
              <p className="text-xl md:text-2xl text-on-surface-variant font-light mb-12 max-w-2xl leading-relaxed">
                Native Android app reading Google Health Connect data with an AI-powered fitness coach and comprehensive health analytics dashboard.
              </p>
              <div className="flex flex-wrap gap-8 items-center">
                <div className="flex flex-col">
                  <span className="text-primary text-4xl font-bold tracking-tighter font-headline">7+</span>
                  <span className="text-on-surface-variant text-sm uppercase tracking-widest font-medium">Health Metrics</span>
                </div>
                <div className="h-12 w-px bg-outline-variant/30"></div>
                <div className="flex flex-col">
                  <span className="text-primary text-4xl font-bold tracking-tighter font-headline">3</span>
                  <span className="text-on-surface-variant text-sm uppercase tracking-widest font-medium">AI Fallback Tiers</span>
                </div>
                <div className="h-12 w-px bg-outline-variant/30"></div>
                <div className="flex flex-col">
                  <span className="text-secondary text-4xl font-bold tracking-tighter font-headline">M3</span>
                  <span className="text-on-surface-variant text-sm uppercase tracking-widest font-medium">Material Design</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Overview */}
        <section className="py-24 px-8 bg-surface-container-lowest">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4 text-white font-headline">Features Overview</h2>
              <p className="text-on-surface-variant max-w-2xl mx-auto">
                A comprehensive health dashboard powered by Google Health Connect, enhanced with AI coaching for personalized fitness guidance.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <div key={index} className="bg-surface-container-low p-6 rounded-xl border border-outline-variant/10 hover:border-primary/30 transition-all group">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4 group-hover:scale-110 transition-transform">
                    {feature.icon}
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2 font-headline">{feature.title}</h3>
                  <p className="text-sm text-on-surface-variant leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* System Stack */}
        <section className="py-24 px-8 bg-surface">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
              <div className="lg:col-span-1">
                <h2 className="text-4xl font-bold mb-6 text-white font-headline">System Stack</h2>
                <p className="text-on-surface-variant mb-8 leading-relaxed">
                  Built entirely on modern Android development best practices with clean architecture, dependency injection, and reactive data flows.
                </p>
                <div className="p-6 bg-surface-container-low rounded-xl border border-outline-variant/10">
                  <Smartphone className="text-primary mb-3" size={28} />
                  <h4 className="font-bold mb-2 text-white">Health Connect Integration</h4>
                  <p className="text-sm text-on-surface-variant leading-relaxed">
                    Unified health data layer aggregating steps, heart rate, sleep, and more from Wear OS devices, fitness trackers, and manual input sources.
                  </p>
                </div>
              </div>
              <div className="lg:col-span-2">
                <div className="overflow-hidden rounded-xl border border-outline-variant/15">
                  <table className="w-full text-left border-collapse">
                    <thead className="bg-surface-container-high text-on-surface uppercase text-xs tracking-widest">
                      <tr>
                        <th className="px-6 py-4">Category</th>
                        <th className="px-6 py-4">Technology</th>
                        <th className="px-6 py-4">Purpose</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline-variant/10">
                      {systemStack.map((row, index) => (
                        <tr key={index} className="hover:bg-surface-container-low transition-colors">
                          <td className="px-6 py-6 font-bold text-primary">{row.category}</td>
                          <td className="px-6 py-6 text-on-surface-variant font-mono text-sm">{row.tech}</td>
                          <td className="px-6 py-6 text-on-surface-variant text-sm">{row.purpose}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Architecture Layers */}
        <section className="py-24 px-8 bg-surface-container-lowest">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4 text-white font-headline">Architecture Layers</h2>
              <p className="text-on-surface-variant max-w-2xl mx-auto">
                Clean architecture with clear separation of concerns, ensuring testability and maintainability at every level.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-surface-container-low p-8 rounded-xl border border-outline-variant/10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                    <Layers size={20} />
                  </div>
                  <h3 className="text-xl font-bold text-white font-headline">UI Layer</h3>
                </div>
                <p className="text-on-surface-variant leading-relaxed">
                  Jetpack Compose screens with Material 3 theming, animated progress indicators, interactive charts using custom Canvas drawing, and responsive layouts adapting to all screen sizes.
                </p>
              </div>
              <div className="bg-surface-container-low p-8 rounded-xl border border-outline-variant/10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                    <Code size={20} />
                  </div>
                  <h3 className="text-xl font-bold text-white font-headline">Domain Layer</h3>
                </div>
                <p className="text-on-surface-variant leading-relaxed">
                  Use case classes orchestrating data operations, health metric calculations, BMI computation, and AI prompt construction. Pure Kotlin with zero Android dependencies for full testability.
                </p>
              </div>
              <div className="bg-surface-container-low p-8 rounded-xl border border-outline-variant/10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center text-secondary">
                    <Shield size={20} />
                  </div>
                  <h3 className="text-xl font-bold text-white font-headline">Data Layer</h3>
                </div>
                <p className="text-on-surface-variant leading-relaxed">
                  Repository pattern abstracting Health Connect API, DataStore for settings, and AI service clients. Coroutine-based async operations with Flow for reactive UI updates.
                </p>
              </div>
              <div className="bg-surface-container-low p-8 rounded-xl border border-outline-variant/10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                    <Brain size={20} />
                  </div>
                  <h3 className="text-xl font-bold text-white font-headline">AI Layer</h3>
                </div>
                <p className="text-on-surface-variant leading-relaxed">
                  Three-tier fallback chain with automatic retry logic. Context-aware prompts incorporating user health data, fitness goals, and conversation history for personalized coaching.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Three-Tier Fallback Chain */}
        <section className="py-24 px-8 bg-surface">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4 text-white font-headline">Three-Tier AI Fallback Chain</h2>
              <p className="text-on-surface-variant max-w-2xl mx-auto">
                Resilient AI coaching that never fails. If one provider is unavailable, the system seamlessly falls back to the next tier.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {aiTiers.map((tier, index) => (
                <div key={index} className="relative bg-surface-container-low p-8 rounded-xl border border-outline-variant/10">
                  {index < 2 && (
                    <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-0.5 bg-gradient-to-r from-tertiary/50 to-transparent"></div>
                  )}
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-xs font-bold text-primary bg-primary/10 px-3 py-1 rounded-full uppercase tracking-widest">{tier.tier}</span>
                    <span className="text-xs font-mono text-on-surface-variant/60">{tier.status}</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-1 font-headline">{tier.name}</h3>
                  <p className="text-sm font-mono text-primary/70 mb-4">{tier.model}</p>
                  <p className="text-sm text-on-surface-variant leading-relaxed">{tier.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Achievements */}
        <section className="py-24 px-8 bg-surface-container-lowest">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-4xl font-bold mb-12 text-white font-headline text-center">Key Achievements</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-surface-container-low p-8 rounded-xl border-l-4 border-primary">
                <div className="flex items-center gap-3 mb-4">
                  <Award className="text-primary" size={24} />
                  <h3 className="text-xl font-bold text-white font-headline">Health Connect Mastery</h3>
                </div>
                <p className="text-on-surface-variant leading-relaxed">
                  Successfully integrated all major Health Connect data types — steps, heart rate, sleep stages, distance, calories, and weight — into a unified, real-time dashboard with proper permission handling and data validation.
                </p>
              </div>
              <div className="bg-surface-container-low p-8 rounded-xl border-l-4 border-primary">
                <div className="flex items-center gap-3 mb-4">
                  <Zap className="text-primary" size={24} />
                  <h3 className="text-xl font-bold text-white font-headline">Resilient AI Architecture</h3>
                </div>
                <p className="text-on-surface-variant leading-relaxed">
                  Designed and implemented a three-tier AI fallback system that ensures 99.9% uptime for fitness coaching. Automatic retry with exponential backoff, circuit breaker patterns, and graceful degradation when all tiers fail.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 px-8 bg-surface">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6 text-white font-headline">Interested in the Code?</h2>
            <p className="text-on-surface-variant mb-10 leading-relaxed">
              The full Android project with Health Connect integration, Jetpack Compose UI, and three-tier AI fallback chain is available for review.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="https://github.com/Viresh26"
                target="_blank"
                rel="noopener noreferrer"
                className="px-10 py-4 bg-primary text-on-tertiary font-headline font-bold rounded-xl transition-all hover:shadow-[0_0_30px_rgba(115,249,0,0.3)]"
              >
                GITHUB REPOSITORY
              </a>
              <Link
                to="/#projects"
                className="px-10 py-4 bg-surface-container-high text-on-surface font-headline font-bold rounded-xl border border-outline-variant/30 hover:bg-surface-container-highest transition-all"
              >
                VIEW ALL PROJECTS
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
    </div>
  )
}
